import {cva, type VariantProps} from 'class-variance-authority';

import {forwardRef} from 'react';

import {getSafely, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {ResponsiveValue, isResponsiveValue} from '../../types/ResponsiveValue';
import {cn} from '../../utils/cn';
import {getResponsiveClassesWithoutPrefix} from '../../utils/getResponsiveClasses';
import {ThemeTextColorPath} from '../Text/Text';

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadingAlign = 'left' | 'center' | 'right';

const HEADING_SIZE_CLASSES: Record<HeadingLevel, string> = {
  1: 'text-[length:var(--heading-size-1)] leading-[var(--heading-leading-1)]',
  2: 'text-[length:var(--heading-size-2)] leading-[var(--heading-leading-2)]',
  3: 'text-[length:var(--heading-size-3)] leading-[var(--heading-leading-3)]',
  4: 'text-[length:var(--heading-size-4)] leading-[var(--heading-leading-4)]',
  5: 'text-[length:var(--heading-size-5)] leading-[var(--heading-leading-5)]',
  6: 'text-[length:var(--heading-size-6)] leading-[var(--heading-leading-6)]',
};

const HEADING_COLOR_CLASSES: Record<ThemeTextColorPath, string> = {
  primary: 'text-[var(--color-primary)]',
  secondary: 'text-[var(--color-secondary)]',
  tertiary: 'text-[var(--color-tertiary)]',
  informational: 'text-[var(--color-informational)]',
  success: 'text-[var(--color-success)]',
  warning: 'text-[var(--color-warning)]',
  danger: 'text-[var(--color-danger)]',
  link: 'text-[var(--color-link)]',
  white: 'text-[var(--color-white)]',
};

const HEADING_ALIGN_CLASSES: Record<HeadingAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const headingVariants = cva('font-heading tracking-[0]', {
  variants: {
    weight: {
      default: 'font-[var(--heading-weight-default)]',
      alternative: 'font-[var(--heading-weight-alternative)]',
    },
    display: {
      block: 'block',
      inline: 'inline',
    },
    truncate: {
      true: 'truncate',
      false: '',
    },
    overflowWrap: {
      anywhere: 'break-all',
      normal: '',
    },
  },
  defaultVariants: {
    weight: 'default',
    display: 'block',
    truncate: false,
    overflowWrap: 'normal',
  },
});

export interface HeadingProps extends TestIdProps, VariantProps<typeof headingVariants> {
  size: HeadingLevel;
  isSingleLine?: boolean;
  alternative?: true;
  headingLevel?: HeadingLevel;
  children?: Nullish | string;
  color?: ThemeTextColorPath;
  overflowWrap?: 'anywhere';
  align?: HeadingAlign | ResponsiveValue<HeadingAlign>;
  isInline?: boolean;
}

/**
 * Resolves align prop to Tailwind classes (supports responsive values)
 */
const getAlignClasses = (
  align: HeadingAlign | ResponsiveValue<HeadingAlign> | undefined
): string => {
  if (align === undefined) {
    return '';
  }

  if (!isResponsiveValue(align)) {
    return getSafely(HEADING_ALIGN_CLASSES, align) ?? '';
  }

  const responsiveClasses = Object.entries(align).reduce((acc, [breakpoint, alignValue]) => {
    if (alignValue === undefined) {
      return acc;
    }
    return {
      ...acc,
      [breakpoint]: getSafely(HEADING_ALIGN_CLASSES, alignValue, ''),
    };
  }, {} as ResponsiveValue<string>);

  return getResponsiveClassesWithoutPrefix(responsiveClasses);
};

/**
 * Heading component - renders headings with consistent typography
 * @example
 * <Heading size={1}>Main Title</Heading>
 * <Heading size={2} color="secondary" alternative>Subtitle</Heading>
 * <Heading size={3} align={{ base: 'center', lg: 'left' }}>Responsive heading</Heading>
 */
export const Heading = forwardRef<HTMLDivElement, HeadingProps>((props, ref) => {
  const sizeClass = HEADING_SIZE_CLASSES[props.size];
  const colorClass = HEADING_COLOR_CLASSES[props.color ?? 'primary'];
  const alignClasses = getAlignClasses(props.align);

  const className = cn(
    headingVariants({
      weight: props.alternative ? 'alternative' : 'default',
      display: props.isInline ? 'inline' : 'block',
      truncate: Boolean(props.isSingleLine),
      overflowWrap: props.overflowWrap === 'anywhere' ? 'anywhere' : 'normal',
    }),
    sizeClass,
    colorClass,
    alignClasses
  );

  return (
    <div
      ref={ref}
      role="heading"
      aria-level={props.headingLevel ?? props.size}
      className={className}
      data-testid={suffixTestId('heading', props)}
    >
      {props.children}
    </div>
  );
});
